/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import useSubscribe, { Subscriber } from '@brands/hooks/useSubscribe';
import { selectAuth } from '@brands/store/selectors/auth';
import React, { useEffect, useMemo, useState } from 'react';
import { isMobile as mobileDetect } from 'react-device-detect';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../hooks/useReduxHook';
import { ICase, Status } from '../../services/cases/types/ICase';
import { getCaseConversations, ICaseConversation } from '../../services/communication/getCaseConversations';
import { getConversation } from '../../services/communication/getConversation';
import { IGMConversation } from '../../services/communication/types/IGMConversation';
import { UserRoleName } from '../../services/identity/types/UserProfile';
import styles from './case-styles.module.scss';
import CaseMessageInfo from './CaseMessageInfo';
import CaseMessageInfoCreateNewConversation from './CaseMessageInfoCreateNewConversation';

const apiTypeToConversationType: Record<string, string> = {
  patient_care_advisor_conversation: `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`,
  patient_provider_conversation: `${UserRoleName.Patient}_${UserRoleName.Provider}`,
  provider_care_advisor_conversation: `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`,
};

interface ConversationsListProps {
  activeConversationSid: string | null;
  setActiveConversationSid: (conversationSid: string) => void;
  thisCase: ICase | undefined;
  setConversations: React.Dispatch<
    React.SetStateAction<
      {
        conversationType: string;
        conversation?: IGMConversation;
        createNewConversation?: boolean;
        conversationCase?: ICase;
        isArchived?: boolean;
      }[]
    >
  >;
  conversations: {
    conversationType: string;
    conversation?: IGMConversation;
    createNewConversation?: boolean;
    conversationCase?: ICase;
    isArchived?: boolean;
  }[];
  setCase: React.Dispatch<React.SetStateAction<ICase | undefined>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  changeCaseUnreadIndicator: (case_id: number) => Promise<void>;
}
const CaseConversationList: React.FC<ConversationsListProps> = ({
  activeConversationSid,
  setActiveConversationSid,
  thisCase,
  setConversations,
  conversations,
  setCase,
  setLoading,
  changeCaseUnreadIndicator,
}) => {
  const { userInfo } = useAppSelector(selectAuth);
  const routeConversationSid = useParams().conversationSid as string;
  const routeCaseId = useParams().caseId as string;
  const [noConversations, setNoConversations] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!conversations || conversations.length === 0) {
        setNoConversations(true);
      }
    }, 8000);

    return () => clearTimeout(timer);
  }, [conversations]);

  useEffect(() => {
    if (conversations && conversations.length > 0) {
      setNoConversations(false);
    }
  }, [conversations]);
  const userRole = useMemo(() => {
    if (!userInfo) {
      return null;
    }

    return userInfo.role.name;
  }, [userInfo]);
  const roleNavigate = useRoleNavigate();
  const isMobile = mobileDetect;

  const getConversationsForCase = async (
    currentCase: ICase,
  ): Promise<
    {
      conversationType: string;
      conversation?: IGMConversation;
      createNewConversation?: boolean;
      isArchived?: boolean;
    }[]
  > => {
    const conversationsList: {
      conversationType: string;
      conversation?: IGMConversation;
      createNewConversation?: boolean;
      conversationCase?: ICase;
      isArchived?: boolean;
    }[] = [];

    const promises: Array<
      Promise<{
        conversationType: string;
        conversation?: IGMConversation;
        createNewConversation?: boolean;
        conversationCase?: ICase;
      }>
    > = [];

    // Threads the case no longer points at (e.g. replaced when a follow-up
    // conversation was created on completion) still hold the message
    // history — surface them as read-only archived entries. Kicked off
    // first so it overlaps the current-thread fetches below; failures are
    // swallowed because archived threads are additive.
    const shouldLoadArchived =
      userRole === UserRoleName.CareAdvisor ||
      userRole === UserRoleName.Provider ||
      userRole === UserRoleName.Admin ||
      userRole === UserRoleName.SuperAdmin ||
      userRole === UserRoleName.OrganizationAdmin ||
      userRole === UserRoleName.Collector ||
      userRole === UserRoleName.Patient;
    // Bound the archived fetch: the shared Axios client has no request
    // timeout, so without this a hung /conversations call would block the
    // whole list (including already-fetched current threads) indefinitely.
    // Archived threads are additive, so degrade to none on timeout/failure.
    const archivedConversationsPromise: Promise<ICaseConversation[]> = shouldLoadArchived
      ? Promise.race([
          getCaseConversations(currentCase.id).catch((error) => {
            console.error('Failed to load archived case conversations', error);
            return [] as ICaseConversation[];
          }),
          new Promise<ICaseConversation[]>((resolve) => {
            setTimeout(() => resolve([]), 8000);
          }),
        ])
      : Promise.resolve([] as ICaseConversation[]);

    if (
      userRole === UserRoleName.CareAdvisor ||
      userRole === UserRoleName.Provider ||
      userRole === UserRoleName.Admin ||
      userRole === UserRoleName.SuperAdmin ||
      userRole === UserRoleName.OrganizationAdmin ||
      userRole === UserRoleName.Collector
    ) {
      if (currentCase.patient_care_advisor_conversation_sid) {
        promises.push(
          Promise.resolve({
            conversationType: `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`,
            conversation: await getConversation(currentCase.patient_care_advisor_conversation_sid),
          }),
        );
      }
      if (currentCase.patient_provider_conversation_sid) {
        promises.push(
          Promise.resolve({
            conversationType: `${UserRoleName.Patient}_${UserRoleName.Provider}`,
            conversation: await getConversation(currentCase.patient_provider_conversation_sid),
          }),
        );
      }
      if (currentCase.provider_care_advisor_conversation_sid) {
        promises.push(
          Promise.resolve({
            conversationType: `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`,
            conversation: await getConversation(currentCase.provider_care_advisor_conversation_sid),
          }),
        );
      }
    } else if (userRole === UserRoleName.Patient) {
      if (currentCase.patient_care_advisor_conversation_sid) {
        promises.push(
          Promise.resolve({
            conversationType: `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`,
            conversation: await getConversation(currentCase.patient_care_advisor_conversation_sid),
          }),
        );
      }
      if (currentCase.patient_provider_conversation_sid) {
        promises.push(
          Promise.resolve({
            conversationType: `${UserRoleName.Patient}_${UserRoleName.Provider}`,
            conversation: await getConversation(currentCase.patient_provider_conversation_sid),
          }),
        );
      }
    }

    const allConversations: {
      conversationType: string;
      conversation?: IGMConversation;
      createNewConversation?: boolean;
      conversationCase?: ICase;
      isArchived?: boolean;
    }[] = await Promise.all(promises);

    const caseConversations = await archivedConversationsPromise;
    const knownSids = new Set(allConversations.map((entry) => entry.conversation?.sid));
    caseConversations
      .filter((conversation) => !conversation.is_current && !knownSids.has(conversation.sid))
      .forEach((conversation) => {
        allConversations.push({
          conversationType: apiTypeToConversationType[conversation.conversation_type] ?? conversation.conversation_type,
          conversation,
          isArchived: true,
        });
      });

    if (userRole === UserRoleName.CareAdvisor && userInfo.id === Number(currentCase.care_advisor_id)) {
      if (currentCase.patient_care_advisor_conversation_sid === null) {
        allConversations.push({
          conversationType: `${UserRoleName.Patient}_${UserRoleName.CareAdvisor}`,
          createNewConversation: true,
        });
      }
      if (
        currentCase.provider_care_advisor_conversation_sid === null &&
        currentCase.provider !== null &&
        currentCase.provider !== undefined
      ) {
        allConversations.push({
          conversationType: `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`,
          createNewConversation: true,
        });
      }
    } else if (userRole === UserRoleName.Provider && userInfo.id === Number(currentCase.provider_id)) {
      if (
        currentCase.patient_provider_conversation_sid === null &&
        (currentCase.status === Status.New ||
          currentCase.status === Status.AcceptedProvider ||
          currentCase.status === Status.InProgressProvider)
      ) {
        allConversations.push({
          conversationType: `${UserRoleName.Patient}_${UserRoleName.Provider}`,
          createNewConversation: true,
        });
      }
      if (
        currentCase.provider_care_advisor_conversation_sid === null &&
        currentCase.care_advisor !== null &&
        currentCase.care_advisor !== undefined
      ) {
        allConversations.push({
          conversationType: `${UserRoleName.Provider}_${UserRoleName.CareAdvisor}`,
          createNewConversation: true,
        });
      }
    }

    return conversationsList.concat(allConversations);
  };

  const reloadConversationList = async (): Promise<void> => {
    if (thisCase) {
      await getConversationsForCase(thisCase).then((convList) => {
        setConversations(convList);
        if (activeConversationSid) {
          const activeConv = convList.find((c) => c?.conversation?.sid === activeConversationSid);
          if (!isMobile && !activeConv) {
            roleNavigate(`/case-chat/${routeCaseId}/${activeConversationSid}`);
          }
        } else if (!isMobile) {
          if (convList[0]?.conversation?.sid) {
            const firstConversarionId = convList[0].conversation.sid;
            setActiveConversationSid(firstConversarionId);
            if (!isMobile) {
              roleNavigate(`/case-chat/${routeCaseId}/${firstConversarionId}`);
            }
          }
        }
      });
    }
  };

  useEffect(() => {
    reloadConversationList();
  }, [thisCase, routeConversationSid, activeConversationSid]);

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage: (message: Record<string, any>) => {
        if (thisCase && Number(message.case_id) === Number(thisCase.id)) {
          if (['case_new_conversation_created', 'case_new_message_created'].includes(message.command)) {
            changeCaseUnreadIndicator(thisCase.id);
            reloadConversationList();
          }
        }
      },
      commands: ['case_new_message_created', 'case_new_conversation_created'],
    }),
    [reloadConversationList],
  );
  const { subscribe, unsubscribe } = useSubscribe();

  useEffect(() => {
    const subscriptionId = subscribe(subscriber);

    return () => unsubscribe(subscriptionId);
  }, [subscribe, unsubscribe, subscriber]);

  const sortConversations = (
    conversations: {
      conversationType: string;
      conversation?: IGMConversation | undefined;
      createNewConversation?: boolean | undefined;
      conversationCase?: ICase;
      isArchived?: boolean | undefined;
    }[],
    userId: number,
  ): {
    conversationType: string;
    conversation?: IGMConversation | undefined;
    createNewConversation?: boolean | undefined;
    conversationCase?: ICase;
    isArchived?: boolean | undefined;
  }[] => {
    return conversations.sort((a, b) => {
      const aIncludesUser = a?.conversation?.participants?.some((p) => Number(p.identity) === Number(userId)) ?? false;
      const bIncludesUser = b?.conversation?.participants?.some((p) => Number(p.identity) === Number(userId)) ?? false;

      const aIsSpecialCase = a.createNewConversation || !a.conversation;
      const bIsSpecialCase = b.createNewConversation || !b.conversation;

      if (aIsSpecialCase && !bIsSpecialCase) return -1;
      if (!aIsSpecialCase && bIsSpecialCase) return 1;

      // Archived (superseded) threads always sort below live ones.
      if (a.isArchived && !b.isArchived) return 1;
      if (!a.isArchived && b.isArchived) return -1;

      if (aIncludesUser && !bIncludesUser) return -1;
      if (!aIncludesUser && bIncludesUser) return 1;

      return 0;
    });
  };

  return (
    <div className={styles.vSidenav}>
      <div className={styles.participantsDiv}>
        <div className={styles.vParticipantsInfo}>
          <div className={styles.messagesList}>
            {conversations && conversations.length > 0 ? (
              sortConversations(conversations, userInfo.id).map((conversation, index) => {
                if (conversation.conversation) {
                  return (
                    <li
                      key={conversation.conversation.sid}
                      className={`${activeConversationSid === conversation.conversation.sid ? styles.selected : ''}
                      ${styles.messagesListList}
                      ${styles.borderBottom}`}
                      onClick={() => {
                        routeCaseId
                          ? roleNavigate(`/case-chat/${routeCaseId}/${conversation.conversation?.sid}`)
                          : roleNavigate(`/messages/${conversation?.conversation?.sid}`);
                      }}
                    >
                      <CaseMessageInfo
                        currentCase={thisCase || conversation.conversationCase}
                        item={conversation.conversation}
                        isSelected={activeConversationSid === conversation.conversation.sid}
                        conversationType={conversation.conversationType}
                        isMobile={isMobile}
                        isArchived={conversation.isArchived}
                      />
                    </li>
                  );
                }
                if (conversation.createNewConversation) {
                  return (
                    <li key={index}>
                      <CaseMessageInfoCreateNewConversation
                        currentCase={thisCase}
                        conversationType={conversation.conversationType}
                        setActiveConversationSid={setActiveConversationSid}
                        setCase={setCase}
                        setLoading={setLoading}
                      />
                    </li>
                  );
                }
                return null;
              })
            ) : !noConversations ? (
              <Loading dotted />
            ) : (
              <span className={styles.noConversationSpan}>No Conversation Found.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseConversationList;
